/**
 * pdfShared.js — PDF 导出共享工具库
 *
 * 提取自 pdfExport / infoCardPdfExport / invoicePdfExport / proposalPdfExport
 * 所有 PDF 模块共享的品牌常量、工具函数和通用渲染器。
 *
 * 使用方式：
 *   import { NAVY, GOLD, PAGE, loadImageAsBase64, ensureSpace, addFooters, savePDF } from './pdfShared';
 */
import { getBRAND_NAME } from './brandConfig';

// ═══════════════════════════════════════════════════════════════════════════════
// Brand & Layout Constants
// ═══════════════════════════════════════════════════════════════════════════════

export const NAVY       = [26, 26, 46];
export const GOLD       = [201, 168, 76];
export const LIGHT_GRAY = [243, 242, 239];
export const PAPER      = [248, 247, 244];
export const TEXT_COLOR = [44, 44, 44];
export const MID_GRAY   = [107, 114, 128];
export const WHITE      = [255, 255, 255];

export const PAGE = { width: 210, height: 297, margin: 15 };
export const CONTENT_WIDTH = PAGE.width - 2 * PAGE.margin;

// ═══════════════════════════════════════════════════════════════════════════════
// Image Loader
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 加载图片并转为 Base64 Data URL。
 * 支持本地路径、远程 URL 和已有的 data: URL。
 * 失败时回退到 LOGO_FALLBACK_URL。
 */
export async function loadImageAsBase64(url, LOGO_FALLBACK_URL) {
  if (!url) return null;
  // 已是 data URL，直接返回
  if (url.startsWith('data:')) return url;

  const tryLoad = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        // Canvas tainted (CORS) — resolve null so caller skips gracefully
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    // Append cache-bust so the CORS fetch bypasses any non-CORS cached entry
    const bust = src.includes('?') ? `${src}&_cb=${Date.now()}` : `${src}?_cb=${Date.now()}`;
    img.src = bust;
  });

  // Retry up to 3 times with 2s delay — handles Cloud Run cold-start 503s
  const tryWithRetry = async (src, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      const result = await tryLoad(src);
      if (result) return result;
      if (i < maxAttempts - 1) await new Promise(r => setTimeout(r, 2000));
    }
    return null;
  };

  const result = await tryWithRetry(url);
  if (result) return result;
  if (LOGO_FALLBACK_URL && url !== LOGO_FALLBACK_URL) {
    return await tryWithRetry(LOGO_FALLBACK_URL);
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Page Layout Utilities
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 检查剩余空间，不足则自动分页。
 * @param {number} footerMargin 页脚保留区域高度（默认 15）
 */
export function ensureSpace(doc, y, needed, footerMargin = 15) {
  if (y + needed > PAGE.height - PAGE.margin - footerMargin) {
    doc.addPage();
    return PAGE.margin;
  }
  return y;
}

/**
 * 自动换行的文本渲染器，遇到页面底部自动分页。
 */
export function addWrappedText(doc, text, x, y, maxWidth, lineHeight, footerMargin = 15) {
  const lines = doc.splitTextToSize(text, maxWidth);
  for (const line of lines) {
    y = ensureSpace(doc, y, lineHeight, footerMargin);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared Page Footers
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 标准品牌页脚：金色分割线 + 居中公司名/页码。
 * 用于 pdfExport、proposalPdf、invoicePdf。
 */
export function addFooters(doc, companyName) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.line(PAGE.margin, PAGE.height - 14, PAGE.width - PAGE.margin, PAGE.height - 14);
    doc.text(
      companyName,
      PAGE.width / 2, PAGE.height - 9,
      { align: 'center' }
    );
  }
}

/**
 * 信息卡页脚：带公司详细信息的变体页脚。
 * 用于 infoCardPdfExport。
 */
export function addInfoCardFooters(doc, companyInfo, brandFullName) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...MID_GRAY);
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.line(PAGE.margin, PAGE.height - 14, PAGE.width - PAGE.margin, PAGE.height - 14);

    const footerParts = [
      companyInfo?.name || brandFullName,
      companyInfo?.director || '',
      companyInfo?.phone || '',
    ].filter(Boolean);
    const footerText = footerParts.join('  |  ');

    doc.text(footerText, PAGE.width / 2, PAGE.height - 9, { align: 'center' });
    doc.text(`Page ${i} of ${totalPages}`, PAGE.width - PAGE.margin, PAGE.height - 9, { align: 'right' });
  }
}

/**
 * 简单页脚：居中 invoice 号 + 页码（无金色分割线）。
 * 用于 invoicePdfExport。
 */
export function addSimpleFooters(doc, label) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MID_GRAY);
    doc.text(
      `${label || 'Document'}   |   Page ${i} of ${total}`,
      PAGE.width / 2,
      PAGE.height - 7,
      { align: 'center' }
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// PDF Save / Download
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 通用 PDF 保存：优先使用 File System Access API (showSaveFilePicker)，
 * 回退到传统 doc.save() 下载。
 */
export async function savePDF(doc, fileName) {
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(doc.output('blob'));
      await writable.close();
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('Save picker failed, falling back to download:', err);
    }
  }
  doc.save(fileName);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Navy Banner Header (shared by pdfExport & invoicePdfExport)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 渲染标准 Navy 横幅 Header，包含 Logo + 公司名 + 联系方式。
 * 返回 header 下方的 y 坐标。
 */
export async function renderNavyHeader(doc, { logoUrl, companyName, acn, address, phone, director, LOGO_FALLBACK_URL, rightSideContent }) {
  const HEADER_H = 42;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, HEADER_H, 'F');

  // Gold bottom accent line
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(0, HEADER_H, PAGE.width, HEADER_H);

  // Logo
  let logoBase64 = null;
  if (logoUrl) {
    logoBase64 = await loadImageAsBase64(logoUrl, LOGO_FALLBACK_URL);
  }

  let logoW = 0;
  if (logoBase64) {
    try {
      const MAX_LOGO_H = 30;
      const MAX_LOGO_W = 50;
      const imgProps = doc.getImageProperties(logoBase64);
      const ratio = imgProps.width / imgProps.height;
      logoW = MAX_LOGO_H * ratio;
      let logoH = MAX_LOGO_H;
      if (logoW > MAX_LOGO_W) {
        logoW = MAX_LOGO_W;
        logoH = MAX_LOGO_W / ratio;
      }
      doc.addImage(logoBase64, 'PNG', PAGE.margin, 6, logoW, logoH);
    } catch (e) {
      console.warn('Logo embed failed:', e);
    }
  }

  const textX = logoW > 0 ? PAGE.margin + logoW + 4 : PAGE.margin;

  // Company name
  doc.setTextColor(...WHITE);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName || getBRAND_NAME(), textX, 14);

  // ACN
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (acn) doc.text(`ACN: ${acn}`, textX, 20);

  // Right side content
  if (rightSideContent) {
    rightSideContent(doc, PAGE.width - PAGE.margin, textX, logoW);
  } else {
    // Default: address + phone + director on right
    const rightX = PAGE.width - PAGE.margin;
    let ry = 10;
    if (address) {
      for (const line of address.split('\n')) {
        doc.text(line.trim(), rightX, ry, { align: 'right' });
        ry += 4;
      }
    }
    if (phone) doc.text(`Phone: ${phone}`, rightX, ry, { align: 'right' });
    if (director) doc.text(`Director: ${director}`, rightX, ry + 4, { align: 'right' });
  }

  return HEADER_H + 7;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Misc Helpers
// ═══════════════════════════════════════════════════════════════════════════════

/** hex 颜色转 RGB 数组 */
export function hexToRgb(hex) {
  if (!hex || !hex.startsWith('#')) return GOLD;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return GOLD;
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
}

/** 格式化日期为 en-AU 长格式 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}
